import { Injectable, inject, signal } from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, distinctUntilChanged } from 'rxjs';

import { GeneralSelectors } from '../../redux/general/general.reducer';

/**
 * Service to manage tab bar visibility based on route data and menu state.
 *
 * Routes with `data: { showTabBar: true }` will show the tab bar.
 * All other routes hide the tab bar by default.
 * Additionally, the tab bar is hidden when the menu is open.
 *
 * Example:
 * {
 *   path: 'cameras',
 *   component: CamerasPage,
 *   data: { showTabBar: true } // Tab bar visible (unless menu is open)
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class TabBarVisibilityService {
  // services
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  /**
   * Signal that emits true when tab bar should be visible.
   * Reads from route data: `{ showTabBar: true }` AND checks if menu is closed.
   * Default: false (hidden) unless explicitly enabled and menu is closed
   */
  readonly tabBarShown$$ = signal<boolean>(this.getRouteData());

  constructor() {
    // Detect route changes and update signal
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.getRouteData()),
        distinctUntilChanged()
      )
      .subscribe(routeAllowsTabBar => {
        const menuIsOpen = this.store.selectSignal(GeneralSelectors.isMenuOpen)();
        this.tabBarShown$$.set(routeAllowsTabBar && !menuIsOpen);
      });

    // Monitor menu state changes
    this.store
      .select(GeneralSelectors.isMenuOpen)
      .pipe(distinctUntilChanged())
      .subscribe(menuIsOpen => {
        const routeAllowsTabBar = this.getRouteData();
        this.tabBarShown$$.set(routeAllowsTabBar && !menuIsOpen);
      });
  }

  /**
   * Get the showTabBar flag from the currently active route data.
   * Traverses the route tree to find the deepest child route.
   */
  private getRouteData(): boolean {
    // Get the root of the current router state
    let route: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;

    // Navigate to the deepest activated child route
    while (route.firstChild) {
      route = route.firstChild;
    }

    // Read showTabBar from route data, default to false if not specified
    return route.data['showTabBar'] === true;
  }
}
